'use client';

import React from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import { trackCTAClick } from '@/lib/analytics';

interface CTASectionProps {
  className?: string;
}

const CTASection = ({ className = '' }: CTASectionProps) => {
  return (
    <section className={`py-16 lg:py-24 bg-gradient-to-br from-primary via-primary/95 to-primary/90 text-white relative overflow-hidden ${className}`}>
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255, 215, 0, 0.3) 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }} />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          {/* Icon */}
          <div className="w-20 h-20 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center mx-auto mb-6">
            <Icon name="ShieldCheckIcon" size={40} className="text-accent" />
          </div>

          {/* Heading */}
          <h2 className="font-headline font-bold text-3xl lg:text-4xl xl:text-5xl mb-6">
            Experience Safety-First Logistics
          </h2>

          {/* Description */}
          <p className="font-body text-lg lg:text-xl text-white/90 mb-8 max-w-2xl mx-auto leading-relaxed">
            Partner with Africa's most trusted logistics provider. Our proven safety record, comprehensive compliance, and transparent operations ensure your cargo arrives securely, every time.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              href="/contact-and-quotes"
              onClick={() => trackCTAClick('Request Safety Report', 'Safety & Compliance', '/contact-and-quotes')}
              className="inline-flex items-center space-x-2 px-8 py-4 bg-gradient-to-r from-accent to-secondary text-primary font-cta font-bold text-base rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group"
            >
              <Icon name="DocumentTextIcon" size={20} />
              <span>Request Safety Report</span>
              <Icon name="ArrowRightIcon" size={20} className="group-hover:translate-x-1 transition-transform duration-300" />
            </Link>

            <Link
              href="/services-overview"
              onClick={() => trackCTAClick('Explore Services', 'Safety & Compliance', '/services-overview')}
              className="inline-flex items-center space-x-2 px-8 py-4 bg-white/10 backdrop-blur-sm border-2 border-white/30 text-white font-cta font-bold text-base rounded-lg hover:bg-white/20 transition-all duration-300 group"
            >
              <Icon name="TruckIcon" size={20} />
              <span>Explore Services</span>
              <Icon name="ArrowRightIcon" size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
          </div>

          {/* Trust Indicators */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mt-12 pt-12 border-t border-white/20">
            {[
              { icon: 'ShieldCheckIcon', label: '99.8% Safety Score' },
              { icon: 'DocumentCheckIcon', label: '15+ Certifications' },
              { icon: 'UserGroupIcon', label: '150+ Active Clients' },
              { icon: 'ClockIcon', label: '24/7 Support' }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <Icon name={item.icon as any} size={32} className="text-accent mx-auto mb-3" />
                <div className="font-body text-sm font-semibold text-white/90">{item.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CTASection;